import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Users, Package, IndianRupee, AlertTriangle, Briefcase, Route, Clock, ArrowLeftRight,
  PackageOpen, FileText, TrendingUp, CircleCheck, Award, Minus, Plus,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { DashboardLayout, StatCard, AmountCard, PageHeader } from '../../common/page_component/index.js';
import AgentScanNoticeCard from '../../qr-scan-notice/AgentScanNoticeCard.js';
import { formatCurrency, formatCurrencyPaise } from '../../../utils/formatCurrency.js';
import { shouldShowDashboardUserTds } from '../../../utils/dashboardTdsDisplay.js';
import {
  getCommissionInsights,
  CommissionSummaryCards,
  CommissionRecentSection,
  CommissionSectionCards,
} from './CommissionDashboardBlocks.js';
import AgentRemittanceSection from './AgentRemittanceSection.js';
import CreditClientsSection from './CreditClientsSection.js';
import CompanyFinanceExtras from './CompanyFinanceExtras.js';
import CargoOverviewSection from './CargoOverviewSection.js';
import CargoDrilldownModal from './CargoDrilldownModal.js';
import MlSheetDetailModal from '../../ml-sheet/MLSheetDetailModal.js';
import BookingSummaryChart, { BOOKING_PAYMENT_COLORS } from './BookingSummaryChart.js';
import ShipmentTrendChart from './ShipmentTrendChart.js';
import CommissionTrendChart from './CommissionTrendChart.js';
import DashboardLineChart from './DashboardLineChart.js';
import DashboardPieChart from './DashboardPieChart.js';
import SectionChartPair from './SectionChartPair.js';
import RecentRecordStrip, { RecentRecordChip } from './RecentRecordStrip.js';
import CollapsiblePanel from './CollapsiblePanel.js';
import ShipmentDetailModal from '../../shipment/ShipmentDetailModal.js';
import SelectField from '../../common/SelectField.js';
import { formatUserWithLocation } from '../../../utils/destinationType.js';
import {
  hasExternalCommissionAccess,
  hasInternalCommissionDashboardAccess,
  hasOnContractAccess,
  hasOnContractTruckAccess,
} from '../../../utils/networkMode.js';
import {
  CHART_DUE_COLOR,
  CHART_NEUTRAL_COLOR,
  CHART_PAID_COLOR,
  CHART_TOOLTIP_WRAPPER_PROPS,
  DashboardAmountTooltip,
} from './chartTheme.js';
import HubMultiSelect from './HubMultiSelect.js';
import RefreshingBanner from './RefreshingBanner.js';
import { effectiveDashboardHubIds } from '../../../utils/dashboardHubFilter.js';

const PIE_COLORS = BOOKING_PAYMENT_COLORS;

const SALARY_MONTH_OPTIONS = [
  ['january', 'January'], ['february', 'February'], ['march', 'March'],
  ['april', 'April'], ['may', 'May'], ['june', 'June'],
  ['july', 'July'], ['august', 'August'], ['september', 'September'],
  ['october', 'October'], ['november', 'November'], ['december', 'December'],
];

const SectionFilters = ({ children }) => (
  <div
    className="grid grid-cols-2 gap-1.5 w-full min-w-0 md:flex md:flex-wrap md:items-center md:justify-end md:w-auto [&>*]:min-w-0 [&>:only-child]:col-span-2"
    onClick={(event) => event.stopPropagation()}
  >
    {children}
  </div>
);

const SectionFilterSelect = ({ value, onChange, options, placeholder }) => (
  <div className="min-w-0 w-full md:w-40">
    <SelectField
      variant="filter"
      value={value}
      onChange={(event) => onChange?.(event.target.value)}
      placeholder={placeholder}
      emptyLabel={placeholder}
      options={[{ value: '', label: placeholder }, ...options]}
    />
  </div>
);

const SectionHubFilter = ({ show, hubs, value, onChange, placeholder }) => (
  show ? (
    <SectionFilters>
      <HubMultiSelect
        compact
        hubs={hubs}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full min-w-0 md:w-44"
      />
    </SectionFilters>
  ) : null
);

const ChartLoading = ({ show }) => (
  show ? (
    <div className="absolute inset-0 z-10 bg-white/70 flex items-center justify-center rounded-lg">
      <div className="w-8 h-8 border-[3px] border-gray-200 border-t-gray-600 rounded-full animate-spin" />
    </div>
  ) : null
);

const hasFigure = (value) => Math.abs(Number(value) || 0) > 0.009;

const chartHasFigure = (chartTrend, keys) => (chartTrend?.points || []).some((point) => (
  keys.some((key) => hasFigure(point?.[key]))
));

const vehicleOptionLabel = (vehicle) => {
  const number = vehicle.vehicle_number || '';
  const name = vehicle.vehicle_name || '';
  return name && number ? `${number} · ${name}` : number || name || String(vehicle.vehicle_id);
};

const OperationsDashboardView = ({
  title,
  headerRight,
  showHubFilter,
  hubs,
  hubIds,
  onHubIdsChange,
  cargoHubIds = [],
  onCargoHubIdsChange,
  bookingHubIds = [],
  onBookingHubIdsChange,
  shipmentHubIds = [],
  onShipmentHubIdsChange,
  cargoData,
  bookingSummary,
  bookingMetric,
  onBookingMetricChange,
  onSummaryDateChange,
  bookingLoading = false,
  cargoLoading = false,
  commissionLoading = false,
  onContractLoading = false,
  tripLoading = false,
  refreshing = false,
  mainRefreshing = false,
  showCommissionAgentFilter = false,
  commissionAgentId = '',
  onCommissionAgentChange,
  onContractVehicleId = '',
  onOnContractVehicleChange,
  tripVehicleId = '',
  onTripVehicleChange,
  tripPartyId = '',
  onTripPartyChange,
  attendanceLoading = false,
  attendanceUserId = '',
  onAttendanceUserChange,
  attendanceHubId = '',
  onAttendanceHubChange,
  salaryLoading = false,
  salaryUserId = '',
  onSalaryUserChange,
  salaryHubId = '',
  onSalaryHubChange,
  salaryMonth = '',
  onSalaryMonthChange,
  salaryYear = '',
  onSalaryYearChange,
  user,
  userData,
  vehicleData,
  shipmentData,
  commissionData,
  onContractData,
  tripContractData,
  attendanceData,
  salaryData,
  mlOutstanding,
  mlAgentDues,
  attendanceEnabled,
  companyAccess,
  extraCards = null,
  dateRange = 'all_time',
  customStart = '',
  customEnd = '',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [drilldown, setDrilldown] = useState({ open: false, bucket: null, fromHubId: null });
  const [shipmentId, setShipmentId] = useState(null);
  const [mlSheetId, setMlSheetId] = useState(null);

  const si = shipmentData?.insights || {};
  const { mlAgent: cma, external: ce, mlAgentRecent, externalRecent } = getCommissionInsights(commissionData);
  const commissionAgents = commissionData?.ml_agent?.agents || [];
  const oi = onContractData?.insights || {};
  const ti = tripContractData?.insights || {};
  const ai = attendanceData?.insights || {};
  const sai = salaryData?.insights || {};
  const pieRows = (bookingSummary?.payment_pie || []).map((row) => ({
    name: row.label,
    value: row.amount,
    key: row.key,
    gc: row.gc_count,
    qty: row.qty,
  }));
  const showMlCommission = hasInternalCommissionDashboardAccess(user);
  const showExternalCommission = hasExternalCommissionAccess(user);
  const showCommission = showMlCommission || showExternalCommission;
  const commissionCardAmount = Number(cma?.total_amount || 0);
  const externalCommissionAmount = Number(
    ce?.total_agent_commission ?? ce?.net_receivable_from_owner ?? 0,
  );
  const showCommissionGraphs = showCommission && (
    commissionCardAmount > 0.009 || externalCommissionAmount > 0.009
  );
  const topCardCount = (user?.role === 'Accountant' || userData)
    ? 3
    : 4 + (user?.role === 'Agent' ? 1 : 0);
  const topCardGridClass = {
    3: 'grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6 items-stretch',
    4: 'grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6 items-stretch',
    5: 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6 items-stretch',
  }[topCardCount] || 'grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6 items-stretch';
  const showOnContract = hasOnContractAccess(user) && (
    hasFigure(oi.total_contracts)
    || hasFigure(oi.total_revenue)
    || hasFigure(oi.total_contract_value)
    || hasFigure(oi.total_trip_pl)
    || hasFigure(oi.total_paid)
    || hasFigure(oi.total_balance)
    || (onContractData?.recent_records || []).length > 0
    || chartHasFigure(onContractData?.chart_trend, ['paid', 'balance'])
  );
  const showTripContract = hasOnContractTruckAccess(user) && (
    hasFigure(ti.total_trips)
    || hasFigure(ti.total_cost)
    || hasFigure(ti.total_balance)
    || hasFigure(ti.total_paid)
    || (tripContractData?.recent_records || []).length > 0
    || chartHasFigure(tripContractData?.chart_trend, ['paid', 'balance'])
  );
  const showShipmentTrend = chartHasFigure(shipmentData?.chart_trend, [
    'paid', 'balance', 'returned', 'missing', 'returned_commission', 'missing_commission',
  ]);
  const showBookingOverview = (bookingSummary?.hubs || []).some((hub) => (
    hasFigure(hub.total) || hasFigure(hub.gc_count) || hasFigure(hub.paid) || hasFigure(hub.to_pay) || hasFigure(hub.on_account)
  )) || pieRows.some((row) => hasFigure(row.value));
  const showAttendanceOverview = Boolean(attendanceEnabled) && (
    hasFigure(ai.total_records)
    || hasFigure(ai.unique_users)
    || hasFigure(ai.currently_logged_in)
    || (attendanceData?.user_breakdown || []).length > 0
    || (attendanceData?.recent_records || []).length > 0
    || chartHasFigure(attendanceData?.chart_trend, ['hours', 'records'])
    || (attendanceData?.status_pie || []).some((row) => hasFigure(row.amount ?? row.value ?? row.count))
  );
  const showSalaryOverview = user?.network_mode !== 'EXTERNAL' && (
    hasFigure(sai.total_entries)
    || hasFigure(sai.total_net_salary)
    || hasFigure(sai.total_basic_salary)
    || hasFigure(sai.total_allowances)
    || hasFigure(sai.total_deductions)
    || hasFigure(sai.total_bonus)
    || hasFigure(sai.total_overtime_pay)
    || (salaryData?.user_breakdown || []).length > 0
    || chartHasFigure(salaryData?.chart_trend, ['paid', 'unpaid'])
  );
  const paidDueLabels = {
    paid: t('dashboard.paidAmount'),
    unpaid: t('dashboard.unpaidAmount'),
    balance: t('dashboard.balanceAmount'),
    completed: t('dashboard.completedSessions'),
    active: t('dashboard.activeSessions'),
  };
  const onContractVehicles = onContractData?.vehicles || [];
  const tripVehicles = tripContractData?.vehicles || [];
  const tripParties = tripContractData?.parties || [];
  const attendanceUsers = attendanceData?.users || [];
  const attendanceHubs = attendanceData?.hubs || [];
  const salaryUsers = salaryData?.users || [];
  const salaryHubs = salaryData?.hubs || [];
  const salaryYears = (salaryData?.years || []).map(String);
  if (salaryYear && !salaryYears.includes(String(salaryYear))) {
    salaryYears.unshift(String(salaryYear));
  }
  const currentYear = String(new Date().getFullYear());
  if (!salaryYears.includes(currentYear)) salaryYears.unshift(currentYear);

  const showDashboardTds = shouldShowDashboardUserTds({
    tdsPercentage: user?.tds_percentage,
    tdsAmount: mlAgentDues?.total_tds_amount,
  });
  return (
    <DashboardLayout>
      <PageHeader title={title}>{headerRight}</PageHeader>
      <RefreshingBanner show={refreshing} />

      <div className={topCardGridClass}>
        {user?.role === 'Accountant' ? (
          <>
            <StatCard
              label="Shipments"
              value={si.total_shipments || 0}
              icon={<Package className="w-5 h-5 text-white" />}
              color="bg-green-600"
            />
            <AmountCard
              label="Total revenue"
              amount={si.total_revenue}
              icon={<IndianRupee className="w-5 h-5 text-white" />}
              color="bg-purple-500"
            />
            <AmountCard
              label="Expenses"
              amount={si.total_expenses || 0}
              icon={<IndianRupee className="w-5 h-5 text-white" />}
              color="bg-rose-600"
              subAmount={si.pending_expenses || 0}
              subLabel="Pending"
              subClassName="text-red-600"
              onClick={() => navigate('/expenses')}
            />
          </>
        ) : userData ? (
          <>
            <StatCard
              label="Shipments"
              value={(
                <span className="block leading-tight">
                  <span className="block text-base sm:text-xl font-bold text-gray-900">{si.total_shipments || 0}</span>
                  <span className="mt-1 block text-[10px] sm:text-[11px] font-medium text-gray-500 leading-snug">
                    Booked {cargoData?.undispatched?.totals?.gc_count || 0}
                    <span className="text-gray-300"> · </span>
                    Dispatched {cargoData?.in_transit?.totals?.gc_count || 0}
                    <span className="text-gray-300"> · </span>
                    Pending {cargoData?.pending_delivery?.totals?.gc_count || 0}
                  </span>
                </span>
              )}
              icon={<Package className="w-5 h-5 text-white" />}
              color="bg-green-600"
            />
            <AmountCard
              label="Total revenue"
              amount={si.total_revenue}
              icon={<IndianRupee className="w-5 h-5 text-white" />}
              color="bg-purple-500"
            />
            <AmountCard
              label="Expenses"
              amount={si.total_expenses || 0}
              icon={<IndianRupee className="w-5 h-5 text-white" />}
              color="bg-rose-600"
              subAmount={si.pending_expenses || 0}
              subLabel="Pending"
              subClassName="text-red-600"
              onClick={() => navigate('/expenses')}
            />
          </>
        ) : (
          <>
            <StatCard
              label="Shipments"
              value={si.total_shipments || 0}
              icon={<Package className="w-5 h-5 text-white" />}
              color="bg-green-600"
            />
            <AmountCard
              label="Total revenue"
              amount={si.total_revenue}
              icon={<IndianRupee className="w-5 h-5 text-white" />}
              color="bg-purple-500"
            />
            <AmountCard
              label="Expenses"
              amount={si.total_expenses || 0}
              icon={<IndianRupee className="w-5 h-5 text-white" />}
              color="bg-rose-600"
              subAmount={si.pending_expenses || 0}
              subLabel="Pending"
              subClassName="text-red-600"
              onClick={() => navigate('/expenses')}
            />
            <AmountCard
              label={t('dashboard.returnToCompany')}
              amount={Math.max(
                0,
                Number(mlOutstanding || 0) - Number(mlAgentDues?.approved_expense_credit || 0),
              )}
              icon={<ArrowLeftRight className="w-5 h-5 text-white" />}
              color="bg-amber-600"
              subAmount={Number(mlAgentDues?.approved_expense_credit || 0) > 0.009
                ? mlAgentDues.approved_expense_credit
                : undefined}
              subLabel={Number(mlAgentDues?.approved_expense_credit || 0) > 0.009
                ? 'Approved expenses'
                : undefined}
            />
            {user?.role === 'Agent' && <AgentScanNoticeCard />}
          </>
        )}
        {extraCards}
      </div>

      <CompanyFinanceExtras
        user={user}
        dateRange={dateRange}
        customStart={customStart}
        customEnd={customEnd}
      />

      {userData && mlAgentDues && mlOutstanding != null && (
        Number(mlOutstanding) > 0
        || Number(mlAgentDues.total_commission_amount) > 0
        || Number(mlAgentDues.total_tds_amount) > 0
        || Number(mlAgentDues.total_gross_to_pay) > 0
        || Number(mlAgentDues.total_paid_freight) > 0
        || Number(mlAgentDues.booking_commission_owed) > 0
      ) && (
        <div className="mb-4 sm:mb-6 rounded-xl border border-amber-200 bg-amber-50/70 p-3 sm:p-4">
          <p className="text-sm font-semibold text-amber-950 mb-2">
            {t('dashboard.returnToCompany')} — remittance
          </p>
          <div className="flex flex-nowrap items-stretch gap-2 overflow-x-auto text-xs sm:text-sm">
            {mlAgentDues.remittance_basis === "both" ? (
              <>
                <div className="shrink-0 min-w-[8.5rem] bg-white/80 rounded-lg border border-amber-100 px-2.5 py-2">
                  <p className="text-amber-800/80 whitespace-nowrap">To-Pay collected</p>
                  <p className="font-semibold tabular-nums text-gray-900">{formatCurrencyPaise(mlAgentDues.total_gross_to_pay)}</p>
                </div>
                <div className="shrink-0 min-w-[8.5rem] bg-white/80 rounded-lg border border-amber-100 px-2.5 py-2">
                  <p className="text-amber-800/80 whitespace-nowrap">Paid freight collected</p>
                  <p className="font-semibold tabular-nums text-gray-900">{formatCurrencyPaise(mlAgentDues.total_paid_freight)}</p>
                </div>
              </>
            ) : (
            <div className="shrink-0 min-w-[8.5rem] bg-white/80 rounded-lg border border-amber-100 px-2.5 py-2">
              <p className="text-amber-800/80 whitespace-nowrap">{mlAgentDues.collected_label || "To-Pay collected"}</p>
              <p className="font-semibold tabular-nums text-gray-900">
                {formatCurrencyPaise(
                  mlAgentDues.remittance_basis === "paid_booking"
                    ? mlAgentDues.total_paid_freight
                    : mlAgentDues.total_gross_to_pay,
                )}
              </p>
            </div>
            )}
            {Number(mlAgentDues.booking_commission_owed) > 0 && (
            <div className="shrink-0 min-w-[8.5rem] bg-white/80 rounded-lg border border-amber-100 px-2.5 py-2">
              <p className="text-amber-800/80 whitespace-nowrap">Booking commission</p>
              <p className="text-[10px] text-amber-700/80">Company pays you</p>
              <p className="font-semibold tabular-nums text-gray-900">{formatCurrencyPaise(mlAgentDues.booking_commission_owed)}</p>
            </div>
            )}
            {Number(mlAgentDues.total_commission_amount) > 0 && (
            <div className="shrink-0 min-w-[8.5rem] bg-white/80 rounded-lg border border-amber-100 px-2.5 py-2">
              <p className="text-amber-800/80 whitespace-nowrap">Delivery commission</p>
              <p className="text-[10px] text-amber-700/80">Kept from collected cash</p>
              <p className="font-semibold tabular-nums text-gray-900">{formatCurrencyPaise(mlAgentDues.total_commission_amount)}</p>
            </div>
            )}
            {Number(mlAgentDues.total_loading_kept) > 0 && (
            <div className="shrink-0 min-w-[7.5rem] bg-white/80 rounded-lg border border-amber-100 px-2.5 py-2">
              <p className="text-amber-800/80 whitespace-nowrap">Loading you keep</p>
              <p className="font-semibold tabular-nums text-gray-900">{formatCurrencyPaise(mlAgentDues.total_loading_kept)}</p>
            </div>
            )}
            {Number(mlAgentDues.total_unloading_kept) > 0 && (
            <div className="shrink-0 min-w-[8rem] bg-white/80 rounded-lg border border-amber-100 px-2.5 py-2">
              <p className="text-amber-800/80 whitespace-nowrap">Unloading you keep</p>
              <p className="font-semibold tabular-nums text-gray-900">{formatCurrencyPaise(mlAgentDues.total_unloading_kept)}</p>
            </div>
            )}
            {showDashboardTds && (
            <div className="shrink-0 min-w-[6rem] bg-white/80 rounded-lg border border-amber-100 px-2.5 py-2">
              <p className="text-amber-800/80 whitespace-nowrap">TDS</p>
              <p className="font-semibold tabular-nums text-gray-900">{formatCurrencyPaise(mlAgentDues.total_tds_amount)}</p>
            </div>
            )}
            <div className="shrink-0 min-w-[8rem] bg-white rounded-lg border border-amber-300 px-2.5 py-2">
              <p className="text-amber-900 font-medium whitespace-nowrap">Send to company</p>
              <p className="font-bold tabular-nums text-amber-950">{formatCurrencyPaise(Math.max(
                0,
                Number(mlOutstanding || 0) - Number(mlAgentDues?.approved_expense_credit || 0),
              ))}</p>
            </div>
          </div>
        </div>
      )}

      <div className="relative">
        <ChartLoading show={cargoLoading} />
        <CargoOverviewSection
          data={cargoData}
          hubFilter={(
            <SectionHubFilter
              show={showHubFilter}
              hubs={hubs}
              value={cargoHubIds}
              onChange={onCargoHubIdsChange}
              placeholder={t('dashboard.allHubs')}
            />
          )}
          onOpenBucket={(bucket, fromHubId) => setDrilldown({ open: true, bucket, fromHubId })}
        />
      </div>

      <AgentRemittanceSection
        user={user}
        dateRange={dateRange}
        customStart={customStart}
        customEnd={customEnd}
      />
      <CreditClientsSection
        user={user}
        dateRange={dateRange}
        customStart={customStart}
        customEnd={customEnd}
      />

      {showShipmentTrend && (
      <div className="relative mb-4 md:mb-6">
        <ChartLoading show={mainRefreshing} />
        <ShipmentTrendChart
          className="!mb-0"
          showCommissionLines={showCommissionGraphs}
          chartTrend={shipmentData?.chart_trend}
          hubFilter={(
            <SectionHubFilter
              show={showHubFilter}
              hubs={hubs}
              value={shipmentHubIds}
              onChange={onShipmentHubIdsChange}
              placeholder={t('dashboard.allHubs')}
            />
          )}
        />
      </div>
      )}

      {showBookingOverview && (
      <div className="relative mb-4 md:mb-6">
        <ChartLoading show={bookingLoading} />
        <CollapsiblePanel
          title={t('dashboard.bookingAndSales')}
          defaultOpen
          trailing={(
            <SectionHubFilter
              show={showHubFilter}
              hubs={hubs}
              value={bookingHubIds}
              onChange={onBookingHubIdsChange}
              placeholder={t('dashboard.allHubs')}
            />
          )}
          className="!mb-0"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <BookingSummaryChart
              embedded
              className="min-w-0"
              data={bookingSummary}
              metric={bookingMetric}
              onMetricChange={onBookingMetricChange}
              onDateChange={onSummaryDateChange}
              loading={bookingLoading}
            />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">{t('dashboard.salesByPaymentType')}</h3>
              {pieRows.length === 0 || !pieRows.some((row) => row.value > 0) ? (
                <p className="text-sm text-gray-400 text-center py-10">{t('common.noData')}</p>
              ) : (
                <div className="grid grid-cols-1 gap-4 items-center">
                  <div className="h-[180px] sm:h-[200px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieRows} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                          {pieRows.map((row) => (
                            <Cell key={row.key} fill={PIE_COLORS[row.key] || '#94a3b8'} />
                          ))}
                        </Pie>
                        <Tooltip
                          {...CHART_TOOLTIP_WRAPPER_PROPS}
                          content={<DashboardAmountTooltip />}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs sm:text-sm">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left py-1">{t('dashboard.paymentStatus')}</th>
                          <th className="text-right py-1">GC</th>
                          <th className="text-right py-1">{t('dashboard.qty')}</th>
                          <th className="text-right py-1">{t('dashboard.netAmount')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(bookingSummary?.payment_pie || []).map((row) => (
                          <tr key={row.key} className="border-t border-gray-50">
                            <td className="py-1.5">{row.label}</td>
                            <td className="text-right">{row.gc_count}</td>
                            <td className="text-right">{row.qty}</td>
                            <td className="text-right">{formatCurrency(row.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CollapsiblePanel>
      </div>
      )}

      {showCommissionGraphs && (
        <CollapsiblePanel
          title={t('dashboard.commissionOverview')}
          defaultOpen={false}
          trailing={
            showMlCommission && showCommissionAgentFilter ? (
              <SectionFilters>
                <SectionFilterSelect
                  value={commissionAgentId}
                  onChange={onCommissionAgentChange}
                  placeholder={t('dashboard.allAgents')}
                  options={commissionAgents.map((agent) => ({
                    value: agent.user_id,
                    label: formatUserWithLocation(agent) || agent.user_id,
                  }))}
                />
              </SectionFilters>
            ) : null
          }
        >
          <div className="relative">
            <ChartLoading show={commissionLoading} />
            <CommissionSectionCards user={user} mlAgent={cma} external={ce} />
            {showMlCommission && (
              <SectionChartPair
                line={<CommissionTrendChart chartTrend={commissionData?.ml_agent?.chart_trend} />}
                pie={(
                  <DashboardPieChart
                    title={t('dashboard.paidVsUnpaid')}
                    rows={commissionData?.ml_agent?.payment_pie}
                    labelMap={paidDueLabels}
                    emptyLabel={t('common.noData')}
                  />
                )}
              />
            )}
            {showExternalCommission && (
              <SectionChartPair
                line={(
                  <CommissionTrendChart
                    chartTrend={commissionData?.external?.chart_trend}
                    title={t('dashboard.receivedVsSettlement')}
                  />
                )}
                pie={(
                  <DashboardPieChart
                    title={t('dashboard.receivedVsSettlement')}
                    rows={commissionData?.external?.payment_pie}
                    labelMap={paidDueLabels}
                    emptyLabel={t('common.noData')}
                  />
                )}
              />
            )}
            <CommissionRecentSection user={user} mlAgentRecent={mlAgentRecent} externalRecent={externalRecent} />
          </div>
        </CollapsiblePanel>
      )}

      {showOnContract && (
        <CollapsiblePanel
          title={t('dashboard.onContractOverview')}
          defaultOpen={false}
          trailing={(
            <SectionFilters>
              <SectionFilterSelect
                value={onContractVehicleId}
                onChange={onOnContractVehicleChange}
                placeholder={t('dashboard.allVehicles')}
                options={onContractVehicles.map((vehicle) => ({
                  value: String(vehicle.vehicle_id),
                  label: vehicleOptionLabel(vehicle),
                }))}
              />
            </SectionFilters>
          )}
        >
          <div className="relative">
            <ChartLoading show={onContractLoading} />
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-4 mb-4">
              <StatCard label={t('dashboard.totalContracts')} value={oi.total_contracts || 0} icon={<Briefcase className="w-5 h-5 text-white" />} color="bg-blue-600" />
              <AmountCard label={t('dashboard.totalRevenue')} amount={oi.total_revenue} icon={<IndianRupee className="w-5 h-5 text-white" />} color="bg-green-600" />
              <AmountCard label={t('dashboard.contractValue')} amount={oi.total_contract_value} icon={<FileText className="w-5 h-5 text-white" />} color="bg-purple-500" />
              <AmountCard label={t('dashboard.tripPL')} amount={oi.total_trip_pl} icon={<TrendingUp className="w-5 h-5 text-white" />} color="bg-indigo-500" />
              <AmountCard label={t('tables.paid')} amount={oi.total_paid} icon={<CircleCheck className="w-5 h-5 text-white" />} color="bg-emerald-600" />
              <AmountCard label={t('tables.balance')} amount={oi.total_balance} icon={<AlertTriangle className="w-5 h-5 text-white" />} color="bg-orange-500" />
            </div>
            <SectionChartPair
              line={(
                <DashboardLineChart
                  t={t}
                  title={t('dashboard.paidVsBalance')}
                  chartTrend={onContractData?.chart_trend}
                  emptyLabel={t('common.noData')}
                  lines={[
                    { dataKey: 'paid', name: t('dashboard.paidAmount'), color: CHART_PAID_COLOR },
                    { dataKey: 'balance', name: t('dashboard.balanceAmount'), color: CHART_DUE_COLOR },
                  ]}
                />
              )}
              pie={(
                <DashboardPieChart
                  title={t('dashboard.paidVsBalance')}
                  rows={onContractData?.payment_pie}
                  labelMap={paidDueLabels}
                  emptyLabel={t('common.noData')}
                />
              )}
            />
            <RecentRecordStrip
              title={t('dashboard.recentOnContract')}
              viewAllLink="/on-contracts"
              items={onContractData?.recent_records || []}
              emptyMsg={t('dashboard.noRecentOnContract')}
              renderItem={(r) => {
                const due = Number(r.balance_amount) > 0;
                return (
                  <RecentRecordChip
                    key={r.contract_id}
                    title={r.contract_id}
                    badge={due ? t('tables.balance') : t('tables.paid')}
                    badgeClass={due ? 'text-red-600' : 'text-green-600'}
                    subtitle={`${r.vehicle_number || '-'} · ${formatCurrency(due ? r.balance_amount : r.paid_amount)}`}
                    onClick={() => navigate(`/on-contracts/view/${r.contract_id}`)}
                  />
                );
              }}
            />
          </div>
        </CollapsiblePanel>
      )}

      {showTripContract && (
        <CollapsiblePanel
          title={t('dashboard.tripContractOverview')}
          defaultOpen={false}
          trailing={(
            <SectionFilters>
              <SectionFilterSelect
                value={tripPartyId}
                onChange={onTripPartyChange}
                placeholder={t('dashboard.allParties')}
                options={tripParties.map((party) => ({
                  value: String(party.contract_party_id),
                  label: party.name || party.company_name || String(party.contract_party_id),
                }))}
              />
              <SectionFilterSelect
                value={tripVehicleId}
                onChange={onTripVehicleChange}
                placeholder={t('dashboard.allVehicles')}
                options={tripVehicles.map((vehicle) => ({
                  value: String(vehicle.vehicle_id),
                  label: vehicleOptionLabel(vehicle),
                }))}
              />
            </SectionFilters>
          )}
        >
          <div className="relative">
            <ChartLoading show={tripLoading} />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 mb-4">
              <StatCard label={t('dashboard.totalTrips')} value={ti.total_trips || 0} icon={<Route className="w-5 h-5 text-white" />} color="bg-blue-600" />
              <AmountCard label={t('dashboard.totalCost')} amount={ti.total_cost} icon={<IndianRupee className="w-5 h-5 text-white" />} color="bg-green-600" subAmount={ti.total_paid} subLabel={t('tables.paid')} />
              <AmountCard label={t('tables.balance')} amount={ti.total_balance} icon={<AlertTriangle className="w-5 h-5 text-white" />} color="bg-orange-500" />
              <AmountCard label={t('dashboard.baseTripCost')} amount={ti.total_base_trip_cost} icon={<IndianRupee className="w-5 h-5 text-white" />} color="bg-purple-500" />
              <AmountCard label={t('dashboard.handlingParkingOther')} amount={(ti.total_handling || 0) + (ti.total_parking || 0) + (ti.total_other || 0)} icon={<PackageOpen className="w-5 h-5 text-white" />} color="bg-indigo-500" />
            </div>
            <SectionChartPair
              line={(
                <DashboardLineChart
                  t={t}
                  title={t('dashboard.paidVsBalance')}
                  chartTrend={tripContractData?.chart_trend}
                  emptyLabel={t('common.noData')}
                  lines={[
                    { dataKey: 'paid', name: t('dashboard.paidAmount'), color: CHART_PAID_COLOR },
                    { dataKey: 'balance', name: t('dashboard.balanceAmount'), color: CHART_DUE_COLOR },
                  ]}
                />
              )}
              pie={(
                <DashboardPieChart
                  title={t('dashboard.paidVsBalance')}
                  rows={tripContractData?.payment_pie}
                  labelMap={paidDueLabels}
                  emptyLabel={t('common.noData')}
                />
              )}
            />
            <RecentRecordStrip
              title={t('dashboard.recentTripContracts')}
              viewAllLink="/trip-contracts"
              items={tripContractData?.recent_records || []}
              emptyMsg={t('dashboard.noRecentTripContracts')}
              renderItem={(r) => {
                const due = Number(r.balance_amount) > 0;
                return (
                  <RecentRecordChip
                    key={r.trip_id}
                    title={r.trip_id}
                    badge={due ? t('tables.balance') : (r.status || t('tables.paid'))}
                    badgeClass={due ? 'text-red-600' : 'text-green-600'}
                    subtitle={`${r.vehicle_number || '-'} · ${formatCurrency(r.total_cost)}`}
                    onClick={() => navigate(`/trip-contracts/view/${r.trip_id}`)}
                  />
                );
              }}
            />
          </div>
        </CollapsiblePanel>
      )}

      {showAttendanceOverview && (
        <CollapsiblePanel
          title={t('dashboard.attendanceOverview')}
          defaultOpen={false}
          trailing={(
            <SectionFilters>
              <SectionFilterSelect
                value={attendanceUserId}
                onChange={onAttendanceUserChange}
                placeholder={t('dashboard.allUsers')}
                options={attendanceUsers.map((person) => ({
                  value: person.user_id,
                  label: formatUserWithLocation(person) || person.name || person.user_id,
                }))}
              />
              <SectionFilterSelect
                value={attendanceHubId}
                onChange={onAttendanceHubChange}
                placeholder={t('dashboard.allHubs')}
                options={attendanceHubs.map((hub) => ({
                  value: String(hub.location_hub_id),
                  label: hub.label || String(hub.location_hub_id),
                }))}
              />
            </SectionFilters>
          )}
        >
          <div className="relative">
            <ChartLoading show={attendanceLoading} />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 mb-4">
              <StatCard label={t('dashboard.totalRecords')} value={ai.total_records || 0} icon={<Clock className="w-5 h-5 text-white" />} color="bg-blue-600" />
              {ai.unique_users != null && (
                <StatCard label={t('dashboard.uniqueUsers')} value={ai.unique_users} icon={<Users className="w-5 h-5 text-white" />} color="bg-green-600" />
              )}
              {ai.currently_logged_in != null && (
                <StatCard label={t('dashboard.currentlyLoggedIn')} value={ai.currently_logged_in} icon={<Users className="w-5 h-5 text-white" />} color="bg-emerald-600" />
              )}
              <StatCard label={t('dashboard.totalWorkHours')} value={ai.total_work_hours || '0h 0m'} icon={<Clock className="w-5 h-5 text-white" />} color="bg-purple-500" />
              <StatCard label={t('dashboard.avgWorkHours')} value={ai.avg_work_hours || '0h 0m'} icon={<Clock className="w-5 h-5 text-white" />} color="bg-indigo-500" />
            </div>
            <SectionChartPair
            line={(
              <DashboardLineChart
                t={t}
                title={t('dashboard.workHoursTrend')}
                chartTrend={attendanceData?.chart_trend}
                format="hours"
                emptyLabel={t('common.noData')}
                lines={[
                  { dataKey: 'hours', name: t('dashboard.hours'), color: CHART_NEUTRAL_COLOR },
                ]}
              />
            )}
            pie={(
              <DashboardPieChart
                title={t('dashboard.sessionStatus')}
                rows={attendanceData?.status_pie}
                format="count"
                labelMap={paidDueLabels}
                emptyLabel={t('common.noData')}
              />
            )}
          />
          <RecentRecordStrip
            title={t('pages.attendance.breakdown')}
            items={attendanceData?.user_breakdown || []}
            emptyMsg={t('dashboard.noAttendanceData')}
            renderItem={(u) => (
              <RecentRecordChip
                key={u.user_id || u.user_name}
                title={u.user_name}
                badge={`${u.days_worked}d`}
                badgeClass="text-gray-500"
                subtitle={u.total_work_hours}
              />
            )}
          />
          <RecentRecordStrip
            title={t('dashboard.recentAttendance')}
            viewAllLink="/attendance"
            items={attendanceData?.recent_records || []}
            emptyMsg={t('dashboard.noRecentAttendance')}
            renderItem={(r) => (
              <RecentRecordChip
                key={r.id}
                title={r.user_name}
                badge={r.is_logged_out ? (r.work_hours || t('dashboard.hours')) : t('dashboard.activeSession')}
                badgeClass={r.is_logged_out ? 'text-green-600' : 'text-blue-600'}
                subtitle={r.attendance_date}
                onClick={() => navigate('/attendance')}
              />
            )}
          />
        </div>
        </CollapsiblePanel>
      )}

      {showSalaryOverview && (
        <CollapsiblePanel
          title={t('dashboard.salaryOverview')}
          defaultOpen={false}
          trailing={(
            <SectionFilters>
              <SectionFilterSelect
                value={salaryMonth}
                onChange={onSalaryMonthChange}
                placeholder={t('dashboard.allMonths')}
                options={SALARY_MONTH_OPTIONS.map(([value, label]) => ({ value, label }))}
              />
              <SectionFilterSelect
                value={salaryYear}
                onChange={onSalaryYearChange}
                placeholder={t('dashboard.allYears')}
                options={salaryYears.map((year) => ({ value: String(year), label: String(year) }))}
              />
              <SectionFilterSelect
                value={salaryUserId}
                onChange={onSalaryUserChange}
                placeholder={t('dashboard.allUsers')}
                options={salaryUsers.map((person) => ({
                  value: person.user_id,
                  label: formatUserWithLocation(person) || person.name || person.user_id,
                }))}
              />
              <SectionFilterSelect
                value={salaryHubId}
                onChange={onSalaryHubChange}
                placeholder={t('dashboard.allHubs')}
                options={salaryHubs.map((hub) => ({
                  value: String(hub.location_hub_id),
                  label: hub.label || String(hub.location_hub_id),
                }))}
              />
            </SectionFilters>
          )}
        >
          <div className="relative">
            <ChartLoading show={salaryLoading} />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 mb-4">
            <AmountCard label={t('dashboard.netSalary')} amount={sai.total_net_salary} icon={<IndianRupee className="w-5 h-5 text-white" />} color="bg-green-600" />
            <AmountCard label={t('dashboard.basicSalary')} amount={sai.total_basic_salary} icon={<IndianRupee className="w-5 h-5 text-white" />} color="bg-blue-600" />
            <AmountCard label={t('dashboard.allowances')} amount={sai.total_allowances} icon={<Plus className="w-5 h-5 text-white" />} color="bg-purple-500" />
            <AmountCard label={t('dashboard.deductions')} amount={sai.total_deductions} icon={<Minus className="w-5 h-5 text-white" />} color="bg-red-500" />
            <AmountCard label={t('dashboard.bonusOT')} amount={(sai.total_bonus || 0) + (sai.total_overtime_pay || 0)} icon={<Award className="w-5 h-5 text-white" />} color="bg-indigo-500" />
          </div>
          <SectionChartPair
            line={(
              <DashboardLineChart
                t={t}
                title={t('dashboard.creditedVsPending')}
                chartTrend={salaryData?.chart_trend}
                emptyLabel={t('common.noData')}
                lines={[
                  { dataKey: 'paid', name: t('dashboard.credited'), color: CHART_PAID_COLOR },
                  { dataKey: 'unpaid', name: t('dashboard.pending'), color: CHART_DUE_COLOR },
                ]}
              />
            )}
            pie={(
              <DashboardPieChart
                title={t('dashboard.creditedVsPending')}
                rows={salaryData?.payment_pie}
                labelMap={{ paid: t('dashboard.credited'), unpaid: t('dashboard.pending') }}
                emptyLabel={t('common.noData')}
              />
            )}
          />
          <RecentRecordStrip
            title={t('pages.salary.breakdown')}
            viewAllLink="/salaries"
            items={salaryData?.user_breakdown || []}
            emptyMsg={t('dashboard.noSalaryData')}
            renderItem={(u) => {
              const pending = Number(u.pending_amount) > 0;
              return (
                <RecentRecordChip
                  key={u.user_id || u.user_name}
                  title={u.user_name}
                  badge={pending ? t('dashboard.pending') : t('dashboard.credited')}
                  badgeClass={pending ? 'text-red-600' : 'text-green-600'}
                  subtitle={formatCurrency(u.total_net_salary)}
                  onClick={() => navigate('/salaries')}
                />
              );
            }}
          />
          </div>
        </CollapsiblePanel>
      )}

      <CargoDrilldownModal
        open={drilldown.open}
        bucket={drilldown.bucket}
        fromHubId={drilldown.fromHubId}
        hubIds={effectiveDashboardHubIds(cargoHubIds, hubIds)}
        onClose={() => setDrilldown({ open: false, bucket: null, fromHubId: null })}
        onOpenShipment={setShipmentId}
        onOpenMlSheet={setMlSheetId}
      />
      <MlSheetDetailModal
        mlSheetId={mlSheetId}
        onClose={() => setMlSheetId(null)}
        onOpenShipment={setShipmentId}
      />
      <ShipmentDetailModal shipmentId={shipmentId} onClose={() => setShipmentId(null)} />
    </DashboardLayout>
  );
};

export default OperationsDashboardView;
